import type { DidYouMeanImpression } from './searchDiagnosticsDidYouMean'
import type {
  SearchDiagnosticResult,
  SearchFailedErrorType,
  SearchSessionEndReason,
} from '~/utils/pendo/search-diagnostics'
import {
  buildSearchSessionEndedPayload,
  SEARCH_DIAGNOSTICS_EVENTS,
} from '~/utils/pendo/search-diagnostics'

export interface RenderedSearchState {
  searchId: string
  query: string
  normalizedQuery: string
  page: number
  renderedAt: number
  totalResults: number
  resultCountOnPage: number
  clickedResult: boolean
  resultClickCount: number
  firstClickedRank: number | null
  filterCount: number
  filterSignature: string
  sortOrder: string
  sessionEnded: boolean
  didYouMeanImpression: DidYouMeanImpression | null
}

export interface CurrentSearchState {
  query: string
  page: number
  routeSearchSignature: string
  isLoading: boolean
  results: SearchDiagnosticResult[]
  totalResults: number
  rows: number
  hasError: boolean
  errorStatus: number | null
  transportRetried: boolean
  filterCount: number
  filterSignature: string
  sortOrder: string
}

export interface AnalyticsState {
  lastRenderedSearch: RenderedSearchState | null
  lastEmittedSignature: string | null
  lastFailedSignature: string | null
}

export type TrackFunction = (eventName: string, payload: Record<string, unknown>) => void

export function finalizeCurrentSearchSession(
  analyticsState: AnalyticsState,
  track: TrackFunction,
  now: () => number,
  endReason: SearchSessionEndReason = 'left_search_page',
): boolean {
  const current = analyticsState.lastRenderedSearch
  if (!current) {
    return false
  }

  return finalizeRenderedSearch(current, track, now(), { endReason })
}

export function finalizeInvalidQuerySession(
  analyticsState: AnalyticsState,
  track: TrackFunction,
  now: () => number,
) {
  if (finalizeCurrentSearchSession(analyticsState, track, now, 'query_invalid')) {
    clearRenderedSearchState(analyticsState)
  }
}

export function finalizePreviousSearchForNextState(
  state: CurrentSearchState,
  analyticsState: AnalyticsState,
  track: TrackFunction,
  endedAt: number,
  reformulated: boolean,
  endOnResultRefresh = true,
): boolean {
  const previous = analyticsState.lastRenderedSearch
  if (!previous) {
    return false
  }

  const usedPagination = previous.page !== state.page
  const changedFilters = previous.filterSignature !== state.filterSignature
  const changedSort = previous.sortOrder !== state.sortOrder
  const endReason = getTransitionEndReason(
    reformulated,
    usedPagination,
    changedFilters,
    changedSort,
    endOnResultRefresh,
  )

  if (!endReason) {
    return false
  }

  return finalizeRenderedSearch(previous, track, endedAt, {
    reformulated,
    continuedSearch: !reformulated && (usedPagination || changedFilters || changedSort),
    usedPagination,
    changedFilters,
    changedSort,
    endReason,
  })
}

export function clearRenderedSearchState(analyticsState: AnalyticsState) {
  analyticsState.lastRenderedSearch = null
  analyticsState.lastEmittedSignature = null
}

export function classifySearchError(statusCode: number | null): SearchFailedErrorType {
  if (statusCode === 400) {
    return 'validation'
  }
  if (statusCode && statusCode >= 500) {
    return 'server'
  }
  return 'unknown'
}

export function buildFailedSearchSignature(
  state: CurrentSearchState,
  normalizedQuery: string,
): string {
  return JSON.stringify({
    query: normalizedQuery,
    page: state.page,
    routeSearchSignature: state.routeSearchSignature,
    statusCode: state.errorStatus,
  })
}

export function normalizeSortOrder(sortOrder: string): string {
  return sortOrder || 'relevance'
}

interface SessionEndContext {
  endReason: SearchSessionEndReason
  reformulated?: boolean
  continuedSearch?: boolean
  usedPagination?: boolean
  changedFilters?: boolean
  changedSort?: boolean
}

function finalizeRenderedSearch(
  renderedSearch: RenderedSearchState,
  track: TrackFunction,
  endedAt: number,
  context: SessionEndContext,
) {
  if (renderedSearch.sessionEnded) {
    return false
  }

  const endReason =
    renderedSearch.clickedResult && context.endReason === 'left_search_page'
      ? 'result_clicked'
      : context.endReason

  track(
    SEARCH_DIAGNOSTICS_EVENTS.sessionEnded,
    buildSessionEndPayload(renderedSearch, endedAt, { ...context, endReason }),
  )

  renderedSearch.sessionEnded = true
  return true
}

function getTransitionEndReason(
  reformulated: boolean,
  usedPagination: boolean,
  changedFilters: boolean,
  changedSort: boolean,
  endOnResultRefresh: boolean,
): SearchSessionEndReason | null {
  if (reformulated) {
    return 'query_changed'
  }
  if (changedFilters) {
    return 'filter_changed'
  }
  if (changedSort) {
    return 'sort_changed'
  }
  if (usedPagination) {
    return 'page_changed'
  }
  return endOnResultRefresh ? 'results_refreshed' : null
}

function buildSessionEndPayload(
  renderedSearch: RenderedSearchState,
  endedAt: number,
  context: SessionEndContext,
) {
  return buildSearchSessionEndedPayload({
    searchId: renderedSearch.searchId,
    query: renderedSearch.query,
    page: renderedSearch.page,
    totalResults: renderedSearch.totalResults,
    resultCountOnPage: renderedSearch.resultCountOnPage,
    clickedResult: renderedSearch.clickedResult,
    resultClickCount: renderedSearch.resultClickCount,
    firstClickedRank: renderedSearch.firstClickedRank,
    reformulated: context.reformulated ?? false,
    continuedSearch: context.continuedSearch ?? false,
    usedPagination: context.usedPagination ?? false,
    changedFilters: context.changedFilters ?? false,
    changedSort: context.changedSort ?? false,
    timeOnSerpMs: endedAt - renderedSearch.renderedAt,
    endReason: context.endReason,
  })
}
